import { useMemo, useState } from 'react'
import { personaStorageKey } from './persona'
import { ViewConfig } from './view-config'

/**
 * User-defined KPI / target values, stored once and referenced by reports.
 *
 * A KPI is a named number (optionally with a unit and a "good when ≥/≤"
 * direction). A report's `threshold` can point at one via `kpiId` instead of
 * hardcoding a value — editing the KPI then moves every linked report, plot and
 * metric at once, and the same KPI can back several reports (e.g. a Number card
 * and a cumulative plot sharing one quota).
 *
 * Prototype-only: localStorage, mirroring `saved-views.ts`. The production
 * version is a KPI table keyed by org.
 */
export type Kpi = {
  id: string
  name: string
  value: number
  unit?: string
  /** Which side of the value is "good"; seeds a linked threshold's direction. */
  dir?: 'above' | 'below'
  createdAt: string
}

export const KPI_STORAGE_KEY = 'reporting-prototype.kpis'

/**
 * Set once the starter KPIs have been seeded, so a user delete isn't undone.
 * Versioned: bump the suffix when adding a curated KPI so existing browsers
 * re-seed once to pick up the new one.
 */
const KPI_SEED_FLAG_KEY = 'reporting-prototype.kpis.seeded.v4'

/**
 * Curated KPIs shipped pre-saved on first load (see STARTER_KPIS), so the
 * library and the "Linked KPI" picker aren't empty out of the box. Fixed
 * id/createdAt (no `Date.now`) so they're stable across reloads, seeded once.
 */
export const STARTER_KPI: Kpi = {
  id: 'kpi-starter-quarterly-revenue',
  name: 'Quarterly revenue target',
  value: 5_000_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Annual won-volume goal, linked by the "Total deals volume won this year" report. */
export const WON_VOLUME_KPI: Kpi = {
  id: 'kpi-annual-won-volume',
  name: 'Annual won volume target',
  value: 2_000_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Monthly open-pipeline volume goal — linked by the "Open Volume LBS" report. */
export const MONTHLY_OPEN_VOLUME_KPI: Kpi = {
  id: 'kpi-monthly-open-volume',
  name: 'Monthly open volume',
  value: 12_000_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Distinct open-customer-opportunity goal — linked by "Open Customer Opps". */
export const OPEN_CUSTOMER_OPPS_KPI: Kpi = {
  id: 'kpi-open-customer-opps',
  name: 'Open customer opps',
  value: 50,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/**
 * Closed-deals count goal — shared by "Deals Won 2026" and "Closed Deals 2026"
 * so both grade against one number (was 70 / 100 hardcoded separately).
 */
export const CLOSED_DEALS_KPI: Kpi = {
  id: 'kpi-closed-deals',
  name: 'Closed deals goal',
  value: 100,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Per-rep monthly-volume goal — linked by "Won LBS by Rep 2026". */
export const REP_VOLUME_KPI: Kpi = {
  id: 'kpi-rep-monthly-volume',
  name: 'Rep monthly volume goal',
  value: 300_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Monthly deal-activity goal (non-email) — linked by "Monthly Deal Activity". */
export const DEAL_ACTIVITY_KPI: Kpi = {
  id: 'kpi-monthly-deal-activity',
  name: 'Monthly deal activity goal',
  value: 700,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

// ── Rep-scoped goals (TM Rep Dashboard) ───────────────────────────────────
// Lower per-rep targets — roughly the team goal split across the ~7 reps, so a
// single rep grades against a reachable number rather than the team total.
/** Per-rep open-pipeline volume goal. */
export const REP_OPEN_VOLUME_KPI: Kpi = {
  id: 'kpi-rep-open-volume',
  name: 'My open volume goal',
  value: 1_800_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Per-rep distinct open-customer goal. */
export const REP_OPEN_CUSTOMER_OPPS_KPI: Kpi = {
  id: 'kpi-rep-open-customer-opps',
  name: 'My open customer opps goal',
  value: 8,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Per-rep closed-deals count goal (shared by my deals-won metric + running line). */
export const REP_CLOSED_DEALS_KPI: Kpi = {
  id: 'kpi-rep-closed-deals',
  name: 'My closed deals goal',
  value: 15,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Per-rep won-volume goal (shared by my won-volume metric + running line). */
export const REP_WON_VOLUME_KPI: Kpi = {
  id: 'kpi-rep-won-volume',
  name: 'My won volume goal',
  value: 300_000,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Per-rep monthly deal-activity goal. */
export const REP_ACTIVITY_KPI: Kpi = {
  id: 'kpi-rep-deal-activity',
  name: 'My deal activity goal',
  value: 100,
  dir: 'above',
  createdAt: '2026-01-01T00:00:00.000Z',
}

/** Curated KPIs shipped pre-saved on first load. */
const STARTER_KPIS: Kpi[] = [
  STARTER_KPI,
  WON_VOLUME_KPI,
  MONTHLY_OPEN_VOLUME_KPI,
  OPEN_CUSTOMER_OPPS_KPI,
  CLOSED_DEALS_KPI,
  REP_VOLUME_KPI,
  DEAL_ACTIVITY_KPI,
  REP_OPEN_VOLUME_KPI,
  REP_OPEN_CUSTOMER_OPPS_KPI,
  REP_CLOSED_DEALS_KPI,
  REP_WON_VOLUME_KPI,
  REP_ACTIVITY_KPI,
]

/** Shape gate for deserialized entries — corrupt/foreign entries dropped silently. */
const isKpi = (v: unknown): v is Kpi => {
  if (typeof v !== 'object' || v === null) return false
  const c = v as Record<string, unknown>
  return (
    typeof c.id === 'string' &&
    typeof c.name === 'string' &&
    typeof c.value === 'number'
  )
}

export function listKpis(): Kpi[] {
  try {
    const raw = localStorage.getItem(personaStorageKey(KPI_STORAGE_KEY))
    if (raw === null) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isKpi)
  } catch {
    return []
  }
}

const writeKpis = (kpis: Kpi[]) =>
  localStorage.setItem(personaStorageKey(KPI_STORAGE_KEY), JSON.stringify(kpis))

export function saveKpi(args: {
  name: string
  value: number
  unit?: string
  dir?: 'above' | 'below'
}): Kpi {
  const kpi: Kpi = {
    ...args,
    id: `kpi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
  }
  writeKpis([...listKpis(), kpi])
  return kpi
}

/** Overwrite an existing KPI in place (keeps id + createdAt). */
export function updateKpi(
  id: string,
  patch: Partial<Omit<Kpi, 'id' | 'createdAt'>>
): Kpi | undefined {
  const list = listKpis()
  const existing = list.find((k) => k.id === id)
  if (!existing) return undefined
  const updated = { ...existing, ...patch }
  writeKpis(list.map((k) => (k.id === id ? updated : k)))
  return updated
}

export function deleteKpi(id: string): void {
  writeKpis(listKpis().filter((k) => k.id !== id))
}

export const getKpi = (id: string): Kpi | undefined =>
  listKpis().find((k) => k.id === id)

/**
 * Seed the starter KPIs on first run only. Flag-guarded so deleting them sticks
 * — they won't reappear on the next load. Kept out of `listKpis`/`saveKpi` so the
 * unit tests (which clear localStorage per case) stay deterministic; called from
 * the React read paths instead (`useKpis`, the library page).
 */
export function seedStarterKpi(): void {
  try {
    if (localStorage.getItem(personaStorageKey(KPI_SEED_FLAG_KEY))) return
    localStorage.setItem(personaStorageKey(KPI_SEED_FLAG_KEY), '1')
    const list = listKpis()
    const missing = STARTER_KPIS.filter((s) => !list.some((k) => k.id === s.id))
    if (missing.length) writeKpis([...missing, ...list])
  } catch {
    /* localStorage unavailable — skip seeding */
  }
}

/**
 * Resolve a config's threshold against the KPI store: when the threshold links a
 * KPI (`kpiId`), overwrite its `value` (and seed `dir`/`label`) from the live KPI
 * so every linked view reflects the latest saved number. The stored `value` is a
 * stale fallback used only when the KPI is missing. Configs without a linked
 * threshold pass through untouched, so non-KPI reports are unaffected.
 */
export function resolveThreshold(config: ViewConfig, kpis: Kpi[]): ViewConfig {
  const threshold = config.threshold
  if (!threshold?.kpiId) return config
  const kpi = kpis.find((k) => k.id === threshold.kpiId)
  if (!kpi) return config
  return {
    ...config,
    threshold: {
      ...threshold,
      value: kpi.value,
      dir: threshold.dir ?? kpi.dir ?? 'above',
      label: threshold.label ?? kpi.name,
    },
  }
}

/**
 * Whether a value clears a goal (threshold): `above` → at or over the target
 * (≥), `below` → at or under (≤); the boundary counts as met. Returns undefined
 * when there's no goal or no value, so callers can leave the cell/badge unstyled.
 * The single source of truth for "is this goal met", shared by the metric badge
 * (MetricView) and the per-row grading on grouped tables (ReportTable).
 *
 * @param value - the measured value (a metric total or a group row's value)
 * @param threshold - the report's goal, if any
 * @returns true if met, false if missed, undefined when not graded
 */
export function goalMet(
  value: number | undefined,
  threshold: ViewConfig['threshold']
): boolean | undefined {
  if (threshold === undefined || value == null) return undefined
  return threshold.dir === 'above'
    ? value >= threshold.value
    : value <= threshold.value
}

/**
 * Read the KPI store for a render. Prototype-grade: localStorage isn't reactive,
 * so `bump()` lets a caller force a re-read after it writes (e.g. after "Save as
 * KPI"). Cross-tab / cross-page edits surface on the next mount.
 */
export function useKpis(): { kpis: Kpi[]; bump: () => void } {
  // Seed once on first mount, before the initial read below.
  const [tick, setTick] = useState(() => {
    seedStarterKpi()
    return 0
  })
  // `tick` is the force-refresh signal: bump() re-reads localStorage. It isn't
  // read in the body, so lint flags it as unnecessary — but dropping it would
  // freeze the list at mount.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const kpis = useMemo(() => listKpis(), [tick])
  return { kpis, bump: () => setTick((t) => t + 1) }
}
